import { memo } from 'react';

import { type Notification } from '@air/api/types';
import { type NotificationsItemProps } from '@air/component-notifications';
import { formatActorNameInitialsOrDefault } from '@air/utilities';

import { NotificationBaseItem } from '~/components/AirNotifications/components/NotificationBaseItem';
import { NotificationClip } from '~/components/AirNotifications/components/NotificationClip';
import { getShortUrlNotificationMeta } from '~/components/AirNotifications/utils/getShortUrlNotificationMeta';
import { CommentDisplay } from '~/components/DiscussionsV2/CommentDisplay';

export type NotificationCommentMentionedTypeProps = Pick<NotificationsItemProps, 'onClick'> & {
  notification: Notification<'commentCreated_userIsMentioned', 'account'>;
};

export const NotificationCommentMentionedType = memo(
  ({ notification, onClick }: NotificationCommentMentionedTypeProps) => {
    const { actor, board, clip, comment } = notification.data;

    const meta = notification.data.shortUrl
      ? getShortUrlNotificationMeta(notification.data.shortUrl)
      : !!board?.title && `in ${board.title}`;

    return (
      <NotificationBaseItem
        content={
          <CommentDisplay
            className="max-h-[64px] ui2026:max-h-none max-w-[300px] ui2026:overflow-visible truncate ui2026:whitespace-pre-wrap ui2026:rounded-8 ads:bg-subtle ui2026:bg-gray-3 ui2026:p-2 text-inherit"
            text={comment?.body}
          />
        }
        meta={meta}
        notification={notification}
        onClick={onClick}
        suffix={<NotificationClip clip={clip} />}
        title={
          <>
            <b>{formatActorNameInitialsOrDefault(actor)}</b>
            {` mentioned you in a comment on `}
            <b>{clip.title}</b>
          </>
        }
      />
    );
  },
);

NotificationCommentMentionedType.displayName = 'NotificationCommentMentionedType';
